import { api } from '../api.js';
import { resolveTenantId } from '../utils/tenantParams.js';

const mordeniotRefreshInflight = new Map();

/**
 * 农场智能设备管理 API：设备档案、地图标注、状态监控、历史记录。
 * 支持设备类型：施肥机、融药机、电磁阀、闸阀、水泵、无人机、气象站、土壤传感器、摄像头
 */
export const farmSmartDeviceApi = {
    list: () => api.get('/farm-smart-device/list', { params: { tenantId: resolveTenantId() } }),

    listEnabled: () => api.get('/farm-smart-device/list-enabled', { params: { tenantId: resolveTenantId() } }),

    listByFarm: (farmId) => api.get('/farm-smart-device/by-farm', { params: { tenantId: resolveTenantId(), farmId } }),

    listByType: (deviceType) => api.get('/farm-smart-device/by-type', { params: { tenantId: resolveTenantId(), deviceType } }),

    listForMap: (farmId = null) => api.get('/farm-smart-device/for-map', { params: { tenantId: resolveTenantId(), farmId } }),

    search: (params = {}) => api.get('/farm-smart-device/search', {
        params: { tenantId: resolveTenantId(), page: 0, size: 20, ...params }
    }),

    getById: (id) => api.get(`/farm-smart-device/${id}`),

    getByCode: (deviceCode) => api.get(`/farm-smart-device/by-code/${deviceCode}`),

    create: (device) => api.post('/farm-smart-device', { tenantId: resolveTenantId(), ...device }),

    /**
     * 分类手动创建（极飞）：唯一键=编码+坐标；型号可先填，同步后修正。
     */
    createManualJifeiCategory: (payload) =>
        api.post('/farm-smart-device/manual-jifei-category', { tenantId: resolveTenantId(), ...payload }),

    update: (id, device) => api.put(`/farm-smart-device/${id}`, device),

    updateLocation: (id, longitude, latitude, installLocation = null) => {
        const body = { longitude, latitude };
        if (installLocation != null && installLocation !== '') {
            body.installLocation = installLocation;
        }
        return api.put(`/farm-smart-device/${id}/location`, body);
    },

    updateStatus: (id, statusPayload) => api.put(`/farm-smart-device/${id}/status`, statusPayload),

    /** 单台拉取极飞实时状态（含电量 power_capacity） */
    refreshJifeiStatus: (id) => api.post(`/farm-smart-device/${id}/refresh-jifei-status`),

    /** 按农场批量同步极飞电量/在线；force=true 时清除 IP 冷却闸门再试；asyncJob=true 立即返回 jobId */
    refreshJifeiStatusByFarm: (farmId, opts = {}) =>
        api.post('/farm-smart-device/refresh-jifei-status', null, {
            params: {
                tenantId: resolveTenantId(),
                farmId,
                force: opts.force === true,
                asyncJob: opts.async === true
            },
            // 同步批量仍可能较久；异步提交很快
            timeout: opts.async === true ? 20000 : 120000
        }),

    /** 传感智控土壤传感器创建（绑定规范化） */
    createMordeniot: (device) =>
        api.post('/farm-smart-device/mordeniot/create', { tenantId: resolveTenantId(), ...device }),

    /** 传感智控土壤传感器更新（绑定规范化，走专用路径） */
    updateMordeniot: (id, device) =>
        api.put(`/farm-smart-device/mordeniot/update/${encodeURIComponent(String(id))}`, device),

    /** 存量：ModernIoT 已绑地块 landId 回写到 iot_device */
    backfillMordeniotParcelBinding: () =>
        api.post('/farm-smart-device/mordeniot/backfill-parcel-binding', {}),

    /** 查询极飞批量刷新异步任务 */
    getJifeiRefreshJob: (jobId) =>
        api.get(`/farm-smart-device/refresh-jifei-status/jobs/${encodeURIComponent(jobId)}`, {
            timeout: 15000
        }),

    /** 轮询直到 DONE/FAILED 或超时，返回任务 data（含 ok/fail/ipBlocked 等） */
    waitJifeiRefreshJob: async (jobId, opts = {}) => {
        const timeoutMs = opts.timeoutMs ?? 90000;
        const intervalMs = opts.intervalMs ?? 1200;
        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
            const res = await api.get(
                `/farm-smart-device/refresh-jifei-status/jobs/${encodeURIComponent(jobId)}`,
                { timeout: 15000 }
            );
            const data = res?.data?.data || {};
            const status = String(data.status || '').toUpperCase();
            if (status === 'DONE' || status === 'FAILED') {
                return data;
            }
            await new Promise((r) => setTimeout(r, intervalMs));
        }
        return null;
    },

    /** 单台拉取传感智控土壤实时数据（同 id 进行中的请求复用，避免 StrictMode/连点打两次） */
    refreshMordeniotStatus: (id, hostId) => {
        const key = String(id || '');
        const hit = mordeniotRefreshInflight.get(key);
        if (hit) return hit;
        const hid = Number(hostId);
        const req = api.post(`/farm-smart-device/${key}/refresh-mordeniot-status`, {}, {
            timeout: 120000,
            params: Number.isFinite(hid) && hid > 0 ? { hostId: hid } : {}
        })
            .finally(() => {
                if (mordeniotRefreshInflight.get(key) === req) mordeniotRefreshInflight.delete(key);
            });
        mordeniotRefreshInflight.set(key, req);
        return req;
    },

    /** 按农场批量同步土壤传感器（ModernIoT，与极飞隔离） */
    refreshMordeniotStatusByFarm: (farmId) =>
        api.post('/farm-smart-device/refresh-mordeniot-status', null, {
            params: { tenantId: resolveTenantId(), farmId },
            timeout: 180000
        }),

    /** 海康摄像头：规范化创建 */
    createHikvision: (device) =>
        api.post('/farm-smart-device/hikvision/create', { tenantId: resolveTenantId(), ...device }),

    /** 海康摄像头：规范化更新 */
    updateHikvision: (id, device) =>
        api.put(`/farm-smart-device/hikvision/update/${id}`, device),

    /** 海康互联云端抓拍准实时预览（JPEG blob；支持 If-None-Match → 304） */
    hikvisionPreview: (id, channelNo, options = {}) =>
        api.get(`/farm-smart-device/${id}/hikvision/preview`, {
            responseType: 'blob',
            timeout: 15000,
            params: {
                ...(channelNo != null ? { channelNo } : {}),
                ...(options.refresh ? { refresh: true } : {}),
                ...(options.quality ? { quality: options.quality } : { quality: 'sd' })
            },
            headers: (!options.refresh && options.etag) ? { 'If-None-Match': options.etag } : undefined,
            validateStatus: (status) => (status >= 200 && status < 300) || status === 304
        }),

    /** 海康截图入库 */
    /** 直连 NVR 视频能力；响应不包含 RTSP 地址或 NVR 凭证。 */
    hikvisionLiveCapability: (id, channelNo) =>
        api.get(`/farm-smart-device/${id}/hikvision/live/capability`, {
            timeout: 15000,
            params: channelNo != null ? { channelNo } : undefined
        }),

    /** 启动 NVR RTSP -> ZLMediaKit -> HTTP-FLV 会话。 */
    hikvisionLiveSession: (id, channelNo) =>
        api.post(
            `/farm-smart-device/${id}/hikvision/live/session`,
            channelNo != null ? { channelNo } : {},
            { timeout: 20000 }
        ),

    hikvisionSnapshot: (id, channelNo) =>
        api.post(
            `/farm-smart-device/${id}/hikvision/snapshot`,
            channelNo != null ? { channelNo } : {},
            { timeout: 60000 }
        ),

    /** 云台控制：action=up|down|left|right|zoomIn|zoomOut|stop */
    hikvisionPtz: (id, action, speed = 50, channelNo, durationMs, persistHome) =>
        api.post(`/farm-smart-device/${id}/hikvision/ptz`, {
            action,
            speed,
            ...(channelNo != null ? { channelNo } : {}),
            ...(Number(durationMs) > 0 ? { durationMs: Number(durationMs) } : {}),
            ...(persistHome ? { persistHome: true } : {})
        }, { timeout: 20000 }),

    hikvisionPtzStatus: (id, channelNo) =>
        api.get(`/farm-smart-device/${id}/hikvision/ptz-status`, {
            timeout: 20000,
            params: channelNo != null ? { channelNo } : undefined
        }),

    /** 光学变倍绝对位置 0–100 */
    hikvisionPtzZoom: (id, zoom, channelNo) =>
        api.put(`/farm-smart-device/${id}/hikvision/ptz-zoom`, {
            zoom,
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 20000 }),

    hikvisionPtzPose: (id, pose = {}, channelNo) =>
        api.put(`/farm-smart-device/${id}/hikvision/ptz-pose`, {
            ...pose,
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 20000 }),

    /** 相对水平转动：direction=left|right，deg 为正角度 */
    hikvisionPtzTurn: (id, deg, direction = 'right', channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/ptz-turn`, {
            deg,
            direction,
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 20000 }),

    hikvisionChannels: (id) =>
        api.get(`/farm-smart-device/${id}/hikvision/channels`, { timeout: 15000 }),

    /** 取流清晰度：0流畅~4极清 */
    hikvisionVideoLevel: (id, channelNo) =>
        api.get(`/farm-smart-device/${id}/hikvision/video-level`, {
            timeout: 20000,
            params: channelNo != null ? { channelNo } : undefined
        }),

    hikvisionPresets: (id, channelNo) =>
        api.get(`/farm-smart-device/${id}/hikvision/presets`, {
            timeout: 20000,
            params: channelNo != null ? { channelNo } : undefined
        }),

    /** 新增预置点：保存当前云台方位 */
    hikvisionAddPreset: (id, name, channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/preset/add`, {
            name: name || undefined,
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 20000 }),

    hikvisionGotoPreset: (id, presetId, channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/preset/goto`, {
            presetId,
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 20000 }),

    /** 设为长期默认位置（预置点或当前朝向），写入设备归位 */
    hikvisionApplyPosition: (id, presetId, channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/position/apply`, {
            ...(presetId > 0 ? { presetId } : {}),
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 30000 }),

    hikvisionPark: (id, enabled, channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/park`, {
            enabled: !!enabled,
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 20000 }),

    hikvisionDeletePreset: (id, presetId, channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/preset/delete`, {
            presetId,
            ...(channelNo != null ? { channelNo } : {})
        }, { timeout: 20000 }),

    hikvisionClearPresets: (id, channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/preset/clear`,
            channelNo != null ? { channelNo } : {},
            { timeout: 120000 }),

    hikvisionRefreshPresetThumbs: (id, channelNo) =>
        api.post(`/farm-smart-device/${id}/hikvision/preset/refresh-thumbs`,
            channelNo != null ? { channelNo } : {},
            { timeout: 180000 }),

    /** 申请对讲通道（返回 streamTalkToken 等） */
    hikvisionTalkSession: (id, channelNo) =>
        api.post(
            `/farm-smart-device/${id}/hikvision/talk/session`,
            channelNo != null ? { channelNo } : {},
            { timeout: 30000 }
        ),

    /**
     * 设备级定时规则（configJson.hikvision.schedules）。
     * 与 IoT 租户级 capture-sync 共用海康云端能力，但编辑面不同：此处是单机动作链；
     * 田间影像页 HikvisionCaptureStatusPanel 只看租户抓拍运行，不配本规则。页面上不解释。
     */
    hikvisionSchedules: (id) =>
        api.get(`/farm-smart-device/${id}/hikvision/schedules`, { timeout: 20000 }),

    hikvisionSaveSchedules: (id, schedules) =>
        api.put(`/farm-smart-device/${id}/hikvision/schedules`, { schedules }, { timeout: 20000 }),

    hikvisionRunSchedule: (id, ruleId) =>
        api.post(`/farm-smart-device/${id}/hikvision/schedules/${encodeURIComponent(ruleId)}/run`, {}, { timeout: 120000 }),

    hikvisionSaveCapturePlan: (id, plan) =>
        api.put(`/farm-smart-device/${id}/hikvision/capture-plan`, plan || {}, { timeout: 20000 }),

    /**
     * 录像转储：创建回放录制任务，返回 data.taskId
     * body: { startTime, endTime, recType: 'local'|'cloud', validateCode?, channelNo?, voiceSwitch?, streamType?, format? }
     */
    hikvisionRecVideoSave: (id, body) =>
        api.post(`/farm-smart-device/${id}/hikvision/rec/video/save`, body || {}, { timeout: 60000 }),

    /** 媒体库图片 → 苗情影像入库 + AI 分析（严重写预警）；农场级设备可传 landParcelId；列表项可传 mediaAssetId */
    hikvisionMediaSeedlingAnalyze: (id, fileName, landParcelId, mediaAssetId) =>
        api.post(
            `/farm-smart-device/${id}/hikvision/media/${encodeURIComponent(fileName)}/seedling-analyze`,
            {},
            {
                timeout: 180000,
                params: {
                    ...(landParcelId ? { landParcelId } : {}),
                    ...(mediaAssetId ? { mediaAssetId } : {})
                }
            }
        ),

    /** 媒体库图片 → 虫情影像入库 + AI 分析（严重写预警）；农场级设备可传 landParcelId；列表项可传 mediaAssetId */
    hikvisionMediaPestAnalyze: (id, fileName, landParcelId, mediaAssetId) =>
        api.post(
            `/farm-smart-device/${id}/hikvision/media/${encodeURIComponent(fileName)}/pest-analyze`,
            {},
            {
                timeout: 180000,
                params: {
                    ...(landParcelId ? { landParcelId } : {}),
                    ...(mediaAssetId ? { mediaAssetId } : {})
                }
            }
        ),

    /** 海康设备媒体库列表 */
    hikvisionMediaList: (id) =>
        api.get(`/farm-smart-device/${id}/hikvision/media`),

    /** 上传图片/短视频到设备媒体库 */
    hikvisionMediaUpload: (id, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/farm-smart-device/${id}/hikvision/media/upload`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000
        });
    },

    /** 删除媒体文件 */
    hikvisionMediaDelete: (id, fileName) =>
        api.delete(`/farm-smart-device/${id}/hikvision/media/${encodeURIComponent(fileName)}`),

    hikvisionCapacities: (id) =>
        api.get(`/farm-smart-device/${id}/hikvision/capacities`, { timeout: 15000 }),

    hikvisionSyncCapacities: (id) =>
        api.post(`/farm-smart-device/${id}/hikvision/capacities/sync`, {}, { timeout: 30000 }),

    refreshHikvisionStatus: (id) =>
        api.post(`/farm-smart-device/${id}/refresh-hikvision-status`),

    /** 按农场批量同步海康摄像头在线（NVR 通道，与极飞隔离） */
    refreshHikvisionStatusByFarm: (farmId) =>
        api.post('/farm-smart-device/refresh-hikvision-status', null, {
            params: { tenantId: resolveTenantId(), farmId },
            timeout: 60000
        }),

    delete: (id) => api.delete(`/farm-smart-device/${id}`),

    summary: () => api.get('/farm-smart-device/summary', { params: { tenantId: resolveTenantId() } }),

    /** 平台管理员：全租户农场智能设备 */
    adminList: (params = {}) => api.get('/farm-smart-device/admin/list', { params }),

    getHistory: (id, params = {}) => api.get(`/farm-smart-device/${id}/history`, { params }),

    /** 极飞云端按日历史状态（farm-device-status-log），date: yyyyMMdd 或 yyyy-MM-dd */
    getJifeiStatusLog: (id, date) =>
        api.get(`/farm-smart-device/${id}/jifei-status-log`, { params: date ? { date } : {} }),

    getStatusStatistics: (id, params = {}) => api.get(`/farm-smart-device/${id}/status-statistics`, { params }),

    getTelemetryHistory: (id, page = 0, size = 20) =>
        api.get(`/farm-smart-device/${id}/telemetry-history`, { params: { page, size } }),

    getCommandHistory: (id, page = 0, size = 20) =>
        api.get(`/farm-smart-device/${id}/command-history`, { params: { page, size } }),

    recordStatus: (id, eventType, statusData, operator) =>
        api.post(`/farm-smart-device/${id}/record-status`, { eventType, statusData, operator }),

    getDeviceTypes: () => api.get('/farm-smart-device/device-types'),

    /** 单设备流程分类绑定列表 */
    listProcessBindings: (id) =>
        api.get(`/farm-smart-device/${id}/process-bindings`, {
            params: { tenantId: resolveTenantId() }
        }),

    /** 覆盖保存流程分类（含 processEnabled / processScope） */
    saveProcessBindings: (id, payload) =>
        api.put(`/farm-smart-device/${id}/process-bindings`, payload, {
            params: { tenantId: resolveTenantId() }
        }),

    /** 按农场拉取流程绑定；不传 processType 时返回该农场全部绑定 */
    listProcessBindingsByFarm: (farmId, processType) =>
        api.get('/farm-smart-device/process-bindings/by-farm', {
            params: {
                tenantId: resolveTenantId(),
                farmId,
                ...(processType ? { processType } : {})
            }
        })
};

export const listUnmatchedCameraMedia = () =>
    api.get('/farm-smart-device/unmatched-media', { params: { tenantId: resolveTenantId() } });

export const previewCameraCoverage = (id) =>
    api.get(`/farm-smart-device/${id}/coverage-preview`);

export const recomputeCameraMediaCoverage = (id) =>
    api.post(`/farm-smart-device/${id}/recompute-media-coverage`);

export const DEVICE_TYPE_LABELS = {
    FERTILIZER_MACHINE: '施肥机',
    MIXING_TANK: '混肥池',
    MEDICINE_MIXER: '融药机',
    FERTIGATOR: '施肥机',
    VALVE: '电磁阀',
    SLUICE: '闸阀',
    PUMP: '水泵',
    INTAKE_PUMP: '取水',
    IRRIGATION_PUMP: '灌溉',
    FPS: 'FPS智能用电开关',
    SWITCH: '智能开关',
    WATER_LEVEL: '水位计',
    LIQUID_LEVEL: '液位计',
    FLOW_METER: '流量计',
    PRESSURE_METER: '压力计',
    FARM_SERVER: '农场服务器',
    FSC: 'FSC农场智能控制器',
    UAV: '无人机',
    WEATHER_STATION: '气象站',
    SOIL_SENSOR: '土壤传感器',
    CAMERA: '摄像头',
    PEST_LAMP: '虫情灯',
    OTHER: '其他设备'
};

export const WORK_STATUS_LABELS = {
    IDLE: '空闲',
    RUNNING: '运行中',
    PAUSED: '暂停',
    FAULT: '故障',
    MAINTENANCE: '维护中'
};

export const WORK_STATUS_COLORS = {
    IDLE: 'default',
    RUNNING: 'processing',
    PAUSED: 'warning',
    FAULT: 'error',
    MAINTENANCE: 'purple'
};

export const DEVICE_TYPE_ICONS = {
    FERTILIZER_MACHINE: 'experiment',
    MIXING_TANK: 'experiment',
    MEDICINE_MIXER: 'medicine-box',
    VALVE: 'control',
    SLUICE: 'gateway',
    PUMP: 'dashboard',
    INTAKE_PUMP: 'dashboard',
    IRRIGATION_PUMP: 'dashboard',
    FPS: 'dashboard',
    SWITCH: 'poweroff',
    WATER_LEVEL: 'dashboard',
    LIQUID_LEVEL: 'dashboard',
    FLOW_METER: 'dashboard',
    PRESSURE_METER: 'dashboard',
    FARM_SERVER: 'gateway',
    FSC: 'control',
    UAV: 'rocket',
    WEATHER_STATION: 'cloud',
    SOIL_SENSOR: 'heat-map',
    CAMERA: 'video-camera',
    PEST_LAMP: 'heat-map',
    OTHER: 'appstore'
};
